import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  createProduct,
  deleteProduct,
  fetchProduct,
  listProducts,
  updateProduct,
  uploadProductPhoto,
} from '@/api/products';
import { queryKeys } from '@/lib/query-keys';
import type { QueryParams } from '@/types/api';
import type {
  CreateProductRequest,
  ProductFilters,
  UpdateProductRequest,
  UploadProductPhotoRequest,
} from '@/types/products';

/**
 * Catálogo de productos del negocio activo (módulo 3).
 *
 * El listado y el detalle los puede leer cualquier miembro; crear/editar/borrar es solo ADMIN
 * (la pantalla esconde los botones y el backend responde 403 igual si alguien los invoca).
 *
 * **El listado está PAGINADO en el servidor (24-sep-2026)**: los filtros (`buscar`, `activo`, `marca`,
 * `categoria`) y la página (`limite`/`offset`) viajan como query params y forman parte de la query key,
 * así que cada combinación tiene su propia caché y `keepPreviousData` evita que la tabla quede en blanco
 * al cambiar de página o de filtro.
 *
 * Las mutaciones invalidan el listado y el detalle en vez de escribir en la caché: `POST`/`PATCH`
 * devuelven el producto guardado, pero el catálogo tiene reglas que conviene volver a leer
 * (el orden por creación y, sobre todo, que un `PATCH` puede haber vaciado campos con `null`).
 */

/** `GET /api/tenants/:tenantId/products` — una página del catálogo, ya filtrada por el servidor. */
export function useProducts(tenantId: string | null, filtros: ProductFilters = {}) {
  return useQuery({
    queryKey: queryKeys.products.list(tenantId ?? '', filtros as QueryParams),
    queryFn: () => listProducts(tenantId ?? '', filtros),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

/**
 * `GET /api/tenants/:tenantId/products/:productId` — un producto del catálogo.
 *
 * Se usa para el detalle (ver `ProductDetailDialog`). Con `productId` vacío la query no corre:
 * así el diálogo puede montarse cerrado sin disparar una petición.
 */
export function useProduct(tenantId: string | null, productId: string | null) {
  return useQuery({
    queryKey: queryKeys.products.detail(tenantId ?? '', productId ?? ''),
    queryFn: () => fetchProduct(tenantId ?? '', productId ?? ''),
    enabled: Boolean(tenantId && productId),
  });
}

function invalidateProducts(queryClient: QueryClient, tenantId: string | null): void {
  if (tenantId) queryClient.invalidateQueries({ queryKey: queryKeys.products.list(tenantId) });
}

/** `POST /api/tenants/:tenantId/products` (solo ADMIN) — responde 201 con el producto creado. */
export function useCreateProduct(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateProductRequest) => createProduct(tenantId ?? '', payload),
    onSuccess: () => invalidateProducts(queryClient, tenantId),
  });
}

/** `PATCH /api/tenants/:tenantId/products/:productId` (solo ADMIN). */
export function useUpdateProduct(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, payload }: { productId: string; payload: UpdateProductRequest }) =>
      updateProduct(tenantId ?? '', productId, payload),
    onSuccess: (product) => {
      invalidateProducts(queryClient, tenantId);
      if (tenantId) {
        queryClient.setQueryData(queryKeys.products.detail(tenantId, product.id), product);
      }
    },
  });
}

/** `DELETE /api/tenants/:tenantId/products/:productId` (solo ADMIN). */
export function useDeleteProduct(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) => deleteProduct(tenantId ?? '', productId),
    onSuccess: (_result, productId) => {
      invalidateProducts(queryClient, tenantId);
      if (tenantId) {
        queryClient.removeQueries({ queryKey: queryKeys.products.detail(tenantId, productId) });
      }
    },
  });
}

/**
 * `POST /api/tenants/:tenantId/products/fotos` (solo ADMIN) — sube UNA foto del equipo y devuelve
 * la URL que hay que guardar en `fotos`.
 *
 * No toca ninguna caché a propósito: la subida todavía NO es parte del catálogo (el producto se
 * guarda después, con esa URL dentro de `fotos`). El que invalida es el `POST`/`PATCH` del producto,
 * que es el que cambia el catálogo.
 */
export function useUploadProductPhoto(tenantId: string | null) {
  return useMutation({
    mutationFn: (payload: UploadProductPhotoRequest) =>
      uploadProductPhoto(tenantId ?? '', payload),
  });
}
